import React, { useEffect, useState } from "react";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EaSeg, inputCls } from "./EaUi";
import { GreenForm, toNum } from "./EaSusGreen";
const str = (v) => (v == null ? "" : String(v).replace(".", ","));
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Valori non validi"; };

function RenRow({ label, sub, val, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-t border-slate-100 first:border-t-0" data-testid={testid}>
      <div className="flex-1 min-w-[180px] text-sm"><b>{label}</b>{sub && <span className="block text-xs text-slate-500">{sub}</span>}</div>
      <EaSeg value={val.mode} onChange={(m) => onChange({ ...val, mode: m })} options={[["pct", "%"], ["mwh", "MWh"]]} testid={`${testid}-mode`} />
      <input inputMode="decimal" value={val.v} onChange={(e) => onChange({ ...val, v: e.target.value })} placeholder={val.mode === "pct" ? "es. 72" : "es. 8200"}
        className={`${inputCls} w-32 text-right`} data-testid={`${testid}-input`} />
    </div>
  );
}

function RenewableForm({ data, points, save }) {
  const init = () => Object.fromEntries([{ code: "" }, ...points].map((p) => {
    const r = data.renewables.find((x) => (x.code || "") === p.code);
    return [p.code, r?.mwh != null ? { mode: "mwh", v: str(r.mwh) } : { mode: "pct", v: str(r?.percentage) }];
  }));
  const [ren, setRen] = useState(init);
  const [showPods, setShowPods] = useState(() => data.renewables.some((r) => r.code));
  useEffect(() => setRen(init()), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = () => {
    const items = Object.entries(ren).map(([code, x]) => ({ code: code || null, n: toNum(x.v), mode: x.mode }));
    if (items.some((i) => Number.isNaN(i.n) || (i.mode === "pct" && i.n > 100))) return toast.error("Inserisci numeri ≥ 0 (percentuale massima 100)");
    save("put", "/ea/sustainability/renewables", { year: data.year, items: items.map((i) => ({ code: i.code, percentage: i.mode === "pct" ? i.n : null, mwh: i.mode === "mwh" ? i.n : null })) }, "Quota rinnovabile salvata");
  };
  const set = (code) => (v) => setRen({ ...ren, [code]: v });
  return (
    <div className="card-elevated p-5" data-testid="sus-renewable-form">
      <div className="font-display font-semibold text-slate-900">Garanzie di Origine / fornitura verde {data.year}</div>
      <div className="text-xs text-slate-500 mb-3">Quota dei prelievi di rete coperta da Garanzie di Origine o fornitura certificata rinnovabile. Indica la percentuale oppure i MWh: il valore del cliente vale per tutto il portfolio, il dato per singolo POD (facoltativo) prevale su quel POD.</div>
      <RenRow label="Intero cliente" sub="Applicata a tutti i POD senza un dato specifico" val={ren[""] || { mode: "pct", v: "" }} onChange={set("")} testid="sus-ren-company" />
      <button onClick={() => setShowPods(!showPods)} className="block text-xs font-semibold text-emerald-700 mt-2" data-testid="sus-ren-toggle-pods">{showPods ? "Nascondi dettaglio per POD" : `Dettaglio per singolo POD (${points.length})`}</button>
      {showPods && points.map((p) => <RenRow key={p.code} label={`POD ${p.identifier}`} sub={[p.label, p.address].filter(Boolean).join(" · ")} val={ren[p.code] || { mode: "pct", v: "" }} onChange={set(p.code)} testid={`sus-ren-${p.identifier}`} />)}
      <button onClick={submit} className="mt-3 h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="sus-ren-save"><Save size={15} /> Salva quota rinnovabile</button>
      <div className="text-xs text-slate-400 mt-1">Lascia vuoto un campo per rimuovere il dato.</div>
    </div>
  );
}

function FactorsForm({ data, save }) {
  const [f, setF] = useState({ p: "", g: "", r: "" });
  useEffect(() => setF({ p: str(data.factors.power_kg_per_kwh), g: str(data.factors.gas_kg_per_smc), r: str(data.factors.residual_kg_per_kwh) }), [data]);
  const submit = () => {
    const p = toNum(f.p), g = toNum(f.g), r = toNum(f.r);
    if (!p || !g || !r) return toast.error("Inserisci fattori numerici maggiori di zero");
    save("put", "/ea/sustainability/factors", { year: data.year, power_kg_per_kwh: p, gas_kg_per_smc: g, residual_kg_per_kwh: r }, "Fattori emissivi personalizzati salvati");
  };
  const gf = data.global_factors;
  return (
    <div className="card-elevated p-5" data-testid="sus-factors-form">
      <div className="font-display font-semibold text-slate-900">Fattori emissivi {data.year}</div>
      <div className="text-xs text-slate-500 mb-3" data-testid="sus-factors-source">In uso: <b>{data.factors.source === "Cliente" ? "valori personalizzati del cliente" : data.factors.source === "Adybo" ? "valori Adybo" : "valori ISPRA"}</b>. Valore Adybo per tutti i clienti: {str(gf.power_kg_per_kwh)} kg CO₂/kWh · {str(gf.gas_kg_per_smc)} kg CO₂/Smc ({gf.source}) · residual mix {str(gf.residual_kg_per_kwh)} kg CO₂/kWh ({gf.residual_source}).</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
        <label className="text-xs text-slate-600">kg CO₂ per kWh elettrico<input inputMode="decimal" value={f.p} onChange={(e) => setF({ ...f, p: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="sus-factor-power" /></label>
        <label className="text-xs text-slate-600">kg CO₂ per Smc gas<input inputMode="decimal" value={f.g} onChange={(e) => setF({ ...f, g: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="sus-factor-gas" /></label>
        <label className="text-xs text-slate-600">Residual mix kg CO₂/kWh (market-based)<input inputMode="decimal" value={f.r} onChange={(e) => setF({ ...f, r: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="sus-factor-residual" /></label>
      </div>
      <div className="text-xs text-slate-500 mt-2 rounded-lg bg-amber-50 border border-amber-100 p-2" data-testid="sus-residual-info"><b>Informativa residual mix.</b> Per lo Scope 2 market-based il GHG Protocol richiede di valorizzare l'energia di rete non coperta da Garanzie di Origine o PPA con il "residual mix", cioè l'intensità emissiva dell'energia non tracciata. Valore di partenza: AIB European Residual Mix per l'Italia; è più alto del mix medio nazionale (location-based), quindi con bassa quota coperta lo Scope 2 market-based risulta superiore.</div>
      <div className="flex gap-2 mt-3">
        <button onClick={submit} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="sus-factors-save"><Save size={15} /> Salva fattori del cliente</button>
        {data.company_factors && <button onClick={() => save("delete", `/ea/sustainability/factors/${data.year}`, null, "Ripristinati i valori Adybo")} className="h-9 px-4 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-2" data-testid="sus-factors-reset"><RotateCcw size={14} /> Ripristina valori Adybo</button>}
      </div>
    </div>
  );
}

function OtherForm({ data, save }) {
  const o = data.avoided_other;
  const [v, setV] = useState("");
  useEffect(() => setV(str(o.manual)), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!o.has_self) return null;
  const submit = () => {
    const n = toNum(v);
    if (Number.isNaN(n)) return toast.error("Inserisci un numero ≥ 0");
    save("put", `/ea/sustainability/avoided-other?year=${data.year}`, { other_eur_mwh: n }, n == null ? "Ripristinato il calcolo automatico" : "Valore salvato");
  };
  return (
    <div className="card-elevated p-5" data-testid="sus-other-form">
      <div className="font-display font-semibold text-slate-900">Costo evitato: altre componenti della bolletta {data.year}</div>
      <div className="text-xs text-slate-500 mb-3">Rete, oneri di sistema, spread e accise risparmiati sull'energia autoconsumata. Calcolo automatico dalle fatture del controllo fatture (costo medio fatturato − PUN medio): <b data-testid="sus-other-auto">{o.auto != null ? `${str(Math.round(o.auto * 100) / 100)} €/MWh` : "non disponibile"}</b>. Inserisci un valore per sostituirlo; lascia vuoto per tornare al calcolo automatico.</div>
      <div className="flex flex-wrap items-center gap-2">
        <input inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} placeholder="€/MWh" className={`${inputCls} w-32 text-right`} data-testid="sus-other-input" />
        <button onClick={submit} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="sus-other-save"><Save size={15} /> Salva</button>
        {o.manual != null && <span className="text-xs text-amber-700" data-testid="sus-other-manual">Valore manuale in uso</span>}
      </div>
    </div>
  );
}

function BaseYear({ data, save }) {
  return (
    <div className="card-elevated p-5 flex flex-wrap items-center gap-3" data-testid="sus-base-year">
      <div className="flex-1 min-w-[220px]"><div className="font-display font-semibold text-slate-900">Anno base del Report ESG</div>
        <div className="text-xs text-slate-500">Anno di riferimento per il confronto delle emissioni (GHG Protocol). Se non impostato si usa il primo anno con dati.</div></div>
      <select value={data.base_year || ""} onChange={(e) => save("put", `/ea/sustainability/base-year?year=${data.year}`, { base_year: e.target.value ? Number(e.target.value) : null }, "Anno base salvato")}
        className={inputCls} data-testid="sus-base-year-select">
        <option value="">Primo anno disponibile</option>
        {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

// Quota rinnovabile e fattori emissivi del cliente per anno: usato dal cliente e da Adybo (Gestione Energy Analytics).
export function EaSusSettings({ companyId, years, year, points, onSaved }) {
  const [y, setY] = useState(year || years?.[0]);
  const [data, setData] = useState(null);
  const params = { company_id: companyId || undefined };
  useEffect(() => { setY(year || years?.[0]); }, [year, companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!y) return undefined;
    let alive = true;
    setData(null);
    api.get("/ea/sustainability/settings", { params: { ...params, year: y } }).then((r) => alive && setData(r.data)).catch(() => alive && toast.error("Impostazioni non disponibili"));
    return () => { alive = false; };
  }, [y, companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (method, url, body, msg) => {
    try {
      const r = method === "put" ? await api.put(url, body, { params }) : await api.delete(url, { params });
      setData(r.data); toast.success(msg); onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!y) return <div className="text-sm text-slate-500">Nessun anno con dati disponibile.</div>;
  return (
    <div className="space-y-4" data-testid="sus-settings">
      <label className="text-sm text-slate-600 inline-flex items-center gap-2">Anno
        <select value={y} onChange={(e) => setY(Number(e.target.value))} className={inputCls} data-testid="sus-settings-year">{years.map((x) => <option key={x} value={x}>{x}</option>)}</select>
      </label>
      {!data ? <div className="text-sm text-slate-400">Caricamento…</div> : (
        <>
          <BaseYear data={data} save={save} />
          <GreenForm data={data} points={points} save={save} params={params} onData={(d) => { setData(d); onSaved?.(); }} />
          <OtherForm data={data} save={save} />
          <RenewableForm data={data} points={points.filter((p) => p.commodity === "POWER")} save={save} />
          <FactorsForm data={data} save={save} />
        </>
      )}
    </div>
  );
}
